import { getPurchaseOrderConfirmedAt } from './purchaseOrderDisplayUtils';

/**
 * Whether a confirm/clear response should update UI state.
 * Later clicks bump generation so stale responses cannot overwrite newer work.
 *
 * @param {number} requestGeneration Generation captured when the request started.
 * @param {number} currentGeneration Latest generation from the component ref.
 * @returns {boolean}
 */
export function shouldApplyConfirmationMutationResult(requestGeneration, currentGeneration) {
    return requestGeneration === currentGeneration;
}

/**
 * Resolves the next `confirmedAtOverride` after detail data changes.
 * Keep the optimistic override until the server confirmed/cleared state matches
 * for a *set* timestamp. Cleared (`null`) overrides are kept for the PO view so a
 * stale refetch cannot restore `confirmed` and hide Confirm PO.
 *
 * @param {Object} args
 * @param {boolean} args.isConfirmationPending
 * @param {string|null|undefined} args.confirmedAtOverride
 * @param {Object|null|undefined} args.purchaseOrderDetail
 * @returns {string|null|undefined}
 */
export function getNextConfirmedAtOverrideAfterServerSync({
    confirmedAtOverride,
    isConfirmationPending,
    purchaseOrderDetail,
}) {
    if (isConfirmationPending) {
        return confirmedAtOverride;
    }
    if (confirmedAtOverride === undefined) {
        return undefined;
    }
    if (!purchaseOrderDetail || !Object.prototype.hasOwnProperty.call(purchaseOrderDetail, 'confirmed')) {
        return confirmedAtOverride;
    }

    const serverConfirmedAt = getPurchaseOrderConfirmedAt(purchaseOrderDetail);
    const overrideCleared = getPurchaseOrderConfirmedAt({ confirmed: confirmedAtOverride }) == null;
    const serverCleared = serverConfirmedAt == null;

    // Keep a cleared override for the rest of this PO view. If we drop it when detail
    // briefly matches, a stale refetch can restore `confirmed` and hide Confirm PO.
    if (overrideCleared) {
        return confirmedAtOverride;
    }

    if (!serverCleared) {
        return undefined;
    }

    return confirmedAtOverride;
}

/**
 * Resolves which confirmed timestamp to show on the entity header/info cards.
 * Override wins when set; otherwise use detail when it has a real `confirmed` value
 * (null counts; empty string does not). Fall back to the search row when detail omits
 * the field or only has an empty string.
 *
 * @param {Object} args
 * @param {string|null|undefined} args.confirmedAtOverride
 * @param {Object|null|undefined} args.purchaseOrderDetail
 * @param {Object|null|undefined} args.primaryPurchaseOrder
 * @returns {string|null}
 */
export function resolvePurchaseOrderConfirmedAt({ confirmedAtOverride, primaryPurchaseOrder, purchaseOrderDetail }) {
    if (confirmedAtOverride !== undefined) {
        return confirmedAtOverride;
    }

    const detailHasConfirmed =
        purchaseOrderDetail != null && Object.prototype.hasOwnProperty.call(purchaseOrderDetail, 'confirmed');

    if (detailHasConfirmed) {
        const detailConfirmedAt = getPurchaseOrderConfirmedAt(purchaseOrderDetail);
        if (detailConfirmedAt != null || purchaseOrderDetail.confirmed == null) {
            return detailConfirmedAt;
        }
    }

    return getPurchaseOrderConfirmedAt(primaryPurchaseOrder);
}

/**
 * Refetch/invalidate after confirm/clear already succeeded.
 * Refresh failures must not look like mutation failures (no rethrow).
 *
 * @param {() => Promise<unknown>} refreshFn
 * @returns {Promise<'refreshed'|'refresh_failed'>}
 */
export async function runPostConfirmationRefresh(refreshFn) {
    try {
        await refreshFn();
        return 'refreshed';
    } catch {
        return 'refresh_failed';
    }
}
